// Long-form SEO content for each service — targeted at Central Florida crane rental keywords.
// Each entry powers a dedicated /services/:slug page and its JSON-LD structured data.

const GROVE = "https://customer-assets.emergentagent.com/job_heavy-lift-solutions-3/artifacts/fabzdy7b_379E0A4F-9557-483E-91F7-55A4134B3FAF-800x600.jpeg";
const MANITEX = "https://customer-assets.emergentagent.com/job_heavy-lift-solutions-3/artifacts/08j7l5sd_IMG_4360.jpeg";

export const SERVICE_CONTENT = {
  "crane-rental-operator": {
    h1: "Crane Rental With Operator in Central Florida",
    metaTitle: "Crane Rental With Operator — DeLand, Orlando & Central FL",
    metaDescription:
      "Mobile crane rental with a certified NCCCO operator from Alliance Crane Service in DeLand, FL. 40-ton Grove & 50-ton Manitex serving Orlando, Daytona, Jacksonville & Titusville.",
    keywords:
      "crane rental with operator central florida, operated crane rental deland, orlando crane rental with operator, manned crane rental florida",
    heroImage: GROVE,
    intro:
      "When you need a crane and someone who knows how to run it, Alliance Crane Service delivers both — every time. Our operated crane rentals include a fully insured mobile crane, an NCCCO-certified operator, a pre-lift plan, and the rigging know-how to put steel exactly where it belongs.",
    body: [
      "Alliance Crane Service is a family-operated crane company based in DeLand, Florida, serving general contractors, mechanical contractors, framers, industrial facilities and property owners across Central Florida. Instead of bare-rental headaches — where you have to source, insure and supervise an operator yourself — we roll in with a complete lift crew ready to go.",
      "Every rental is priced with the operator, fuel, mobilization and standard rigging included. You get a single point of contact, a certificate of insurance for your GC, and a crew that has been on hundreds of Florida jobsites — from Volusia County to Duval County.",
    ],
    benefits: [
      { title: "NCCCO-Certified Operators", body: "Current NCCCO certification, OSHA-30 training and clean drug-screen records on every operator." },
      { title: "Full Coverage Insured", body: "General liability + umbrella policy. COIs available same-day for your GC." },
      { title: "Pre-Lift Planning", body: "Rated capacity checks, ground-bearing review, swing radius and clearance analysis before we roll a wheel." },
      { title: "Signal-Person Training", body: "Our operators speak the same signal language your steel crew and ironworkers do." },
    ],
    applications: [
      "Commercial and light-industrial construction",
      "Rooftop HVAC unit swaps and new installs",
      "Pre-engineered metal building erection",
      "Church and school steeples/spires",
      "Modular building drops",
      "Sign and light-pole installation",
      "Utility, telecom and antenna work",
    ],
    faqs: [
      {
        q: "Do you provide the operator or is that separate?",
        a: "Every Alliance Crane Service rental includes an NCCCO-certified operator. We do not offer bare-crane rentals — safety and quality control are the reasons we started the company.",
      },
      {
        q: "How much does an operated crane rental cost in Central Florida?",
        a: "Pricing depends on crane size (40-ton vs 50-ton), day rate vs half-day, jobsite location, and the number of picks. Most single-day residential jobs in the DeLand/Orlando area fall in a predictable range — call (386) 506-7771 for a same-day quote.",
      },
      {
        q: "How much notice do you need to book?",
        a: "For non-emergency work, 3–7 days notice is ideal. We regularly book same-week for HVAC and truss work, and we operate a 24/7 emergency line for storm and accident lifts.",
      },
      {
        q: "Which cities do you cover?",
        a: "DeLand, Orlando, Daytona Beach, Sanford, Lake Mary, Ormond Beach, New Smyrna Beach, Kissimmee, Melbourne, Palm Coast, Jacksonville and Titusville — plus most of Central and North Florida on request.",
      },
    ],
    related: ["hydraulic-mobile-crane", "hvac-placement", "steel-erection"],
  },

  "hydraulic-mobile-crane": {
    h1: "Hydraulic Mobile Crane Services — DeLand & Central Florida",
    metaTitle: "Hydraulic Mobile Crane Services — Orlando, DeLand, Daytona",
    metaDescription:
      "Highway-legal hydraulic mobile crane services across Central Florida. Fast-setup 40-ton and 50-ton boom trucks for one-day picks, tight sites and infill construction.",
    keywords:
      "hydraulic mobile crane florida, mobile crane rental orlando, boom truck crane deland, telescopic crane central florida",
    heroImage: MANITEX,
    intro:
      "Alliance Crane Service runs two hydraulic mobile cranes — a 40-ton Grove and a 50-ton Manitex — engineered for exactly the type of quick-turn, high-mobility lifts most Central Florida jobs demand. Highway-legal, urban-friendly and fast to set up.",
    body: [
      "A hydraulic mobile crane rides in on its own chassis, sets outriggers in minutes and can be lifting within thirty minutes of arrival. That makes it the right tool for HVAC swaps, truss drops, sign installs, cell tower work, retail buildouts and mechanical yard picks where you do not have the time or the room for a rough-terrain or crawler crane.",
      "Because we run modern hydraulic booms with load-moment indicators, we can safely reach further and swing tighter than older cable-boom equipment — which matters on Central Florida jobsites where set-back requirements, live power lines and adjacent buildings are non-negotiable constraints.",
    ],
    benefits: [
      { title: "Rapid Setup", body: "Outriggers deployed and boom in the air fast — great for single-pick and half-day scopes." },
      { title: "Highway Legal", body: "No permit lead-times on standard configurations. We can be onsite same-day for local work." },
      { title: "Tight-Site Friendly", body: "Compact footprints and telescopic booms squeeze into infill and residential lots." },
      { title: "Load-Moment Indicators", body: "Modern electronics protect the load, the crane and your project." },
    ],
    applications: [
      "HVAC rooftop unit placement",
      "Truss and beam lifts",
      "Retail and commercial buildouts",
      "Sign and monument installations",
      "Antenna, satellite and telecom picks",
      "Machinery relocation and equipment sets",
      "One-day utility work",
    ],
    faqs: [
      {
        q: "What is a hydraulic mobile crane?",
        a: "A hydraulic mobile crane — sometimes called a boom truck or hydraulic truck crane — is a self-propelled telescopic-boom crane mounted on a road-legal truck chassis. It combines fast mobility with meaningful lifting capacity, making it the workhorse of most jobsites.",
      },
      {
        q: "What is the difference between your 40-ton Grove and 50-ton Manitex?",
        a: "The 40-ton Grove is our versatile daily driver — perfect for HVAC, residential and mid-reach work. The 50-ton Manitex has a longer boom, higher capacity and a heavier chassis for steel erection, machinery moves and longer-reach commercial picks.",
      },
      {
        q: "Do you need special permits?",
        a: "For standard configurations, no. Long-distance transport and oversize loads may require permits — our dispatch handles it as part of the mobilization quote when needed.",
      },
    ],
    related: ["crane-rental-operator", "truss-lifts", "machinery-moving"],
  },

  "hvac-placement": {
    h1: "HVAC Equipment Placement in Central Florida",
    metaTitle: "HVAC Crane Lifting & Rooftop Unit Placement — Central FL",
    metaDescription:
      "Rooftop AHU, RTU, chiller and split-system crane lifts across Orlando, DeLand, Daytona, Jacksonville & Titusville. Coordinated HVAC equipment placement with certified operators.",
    keywords:
      "hvac crane lift florida, rooftop unit placement orlando, ac unit crane lift deland, chiller lift central florida, rtu crane rental",
    heroImage: "https://images.pexels.com/photos/19016904/pexels-photo-19016904.jpeg",
    intro:
      "Mechanical contractors across Central Florida call Alliance Crane Service when a rooftop unit, air handler or chiller needs to land safely and in the right spot the first time. From single residential change-outs to multi-unit commercial rooftops, we handle the lift so your HVAC crew can focus on the connections.",
    body: [
      "HVAC placement is one of the most common — and most weather-sensitive — crane jobs in Florida. We work around your delivery windows, your electrician, your roofer and the building's occupancy schedule to get units in the air and on the curb quickly.",
      "Whether you are swapping out a failed condenser at a strip mall on a Saturday morning, staging a bank of new RTUs on a warehouse in Ormond Beach, or landing a chiller on a hospital rooftop in Orlando, our operators have done it before and know how to protect your equipment and the building.",
    ],
    benefits: [
      { title: "Delivery Coordination", body: "We arrive when the truck arrives — no dead time on your invoice." },
      { title: "Rooftop Experience", body: "Membrane-safe operations, curb alignment, and clean, damage-free sets." },
      { title: "Emergency Change-Outs", body: "Failed unit on a hot day? Call our 24/7 line — same-day service is often possible." },
      { title: "Multi-Unit Staging", body: "New builds with 6+ units? We stage efficiently to keep your mechanical crew moving." },
    ],
    applications: [
      "RTU (rooftop unit) sets and swaps",
      "Air handling unit (AHU) placement",
      "Chiller and cooling tower lifts",
      "Split-system condenser sets",
      "Kitchen exhaust hood and MUA unit installs",
      "Emergency HVAC replacements",
      "Multi-unit new-construction staging",
    ],
    faqs: [
      {
        q: "Do you work with our HVAC subcontractor directly?",
        a: "Yes. We routinely coordinate directly with mechanical contractors on schedule, rigging plan and unit specs. Send us the equipment cut sheets and roof location and we will handle the rest.",
      },
      {
        q: "Can you set a rooftop unit on a Saturday?",
        a: "Yes — weekend, evening and after-hours HVAC lifts are common for us. We accommodate retail and school schedules whenever we can.",
      },
      {
        q: "How high can you reach?",
        a: "Our 50-ton Manitex reaches roughly 155 ft with the main boom and further with the optional jib. That covers virtually every rooftop RTU and chiller job in Central Florida.",
      },
      {
        q: "Do you protect the roof membrane?",
        a: "Absolutely. We use skidboards, crane mats and outrigger pads where required and coordinate with the roofing contractor when a set is over new membrane.",
      },
    ],
    related: ["crane-rental-operator", "emergency-lifting", "machinery-moving"],
  },

  "steel-erection": {
    h1: "Steel Erection Crane Services — Central Florida",
    metaTitle: "Steel Erection Crane Rental — Orlando, DeLand & Jacksonville",
    metaDescription:
      "Structural steel erection cranes in Central Florida. 40-ton Grove & 50-ton Manitex for warehouses, retail shells, pre-engineered metal buildings and light-industrial steel.",
    keywords:
      "steel erection crane florida, structural steel lift orlando, pre-engineered metal building crane, warehouse steel erection central florida",
    heroImage: "https://images.unsplash.com/photo-1493476523860-a6de6ce1b0c3",
    intro:
      "From pre-engineered metal buildings to structural steel skeletons for retail, restaurants and light-industrial buildings, Alliance Crane Service delivers the crane, the operator and the pace your ironworkers need. We keep the sequence moving and the connection points on center.",
    body: [
      "Steel work is one of the most safety-critical scopes on any jobsite. We plan every lift with the erector: pick weights, rigging, sequence, swing radius, load path, wind speed and set-down location. On the day of the pour, we mobilize early, hit the huddle, and lift on the erector's schedule — not ours.",
      "Our 50-ton Manitex is the primary machine for steel erection scopes; the 40-ton Grove handles secondary picks, purlins, girts and staging. Between the two, we can support most single-story and low-rise steel jobs common across Central Florida.",
    ],
    benefits: [
      { title: "Erector-Friendly", body: "We work with ironworkers, not against them — signals, pace and sequencing." },
      { title: "Tight Tolerance Picks", body: "Modern LMI-equipped cranes let us set steel on center, on grade." },
      { title: "Documented Lift Plans", body: "Signed critical-lift plans available when your GC or owner requires them." },
      { title: "Safety-First Culture", body: "Daily inspections, pre-lift huddles and stop-work authority for every crew member." },
    ],
    applications: [
      "Warehouse and distribution buildings",
      "Retail and restaurant shells",
      "Pre-engineered metal buildings (PEMB)",
      "Church and community-building steel",
      "Bar joists, girders and columns",
      "Rooftop equipment platforms",
      "Canopy and porte-cochere framing",
    ],
    faqs: [
      {
        q: "Can you handle a full pre-engineered metal building?",
        a: "Yes. Our 50-ton Manitex is well-suited for PEMB erection of typical Central Florida size classes. We coordinate with your steel supplier and erector on sequencing.",
      },
      {
        q: "Do you provide a lift plan?",
        a: "Absolutely. For any critical lift, or when the GC or engineer requires one, we provide a written, signed lift plan.",
      },
      {
        q: "What is your maximum single-pick capacity?",
        a: "50 tons on the Manitex under ideal conditions, derated based on radius. We size the pick during the pre-job walk.",
      },
    ],
    related: ["crane-rental-operator", "hydraulic-mobile-crane", "truss-lifts"],
  },

  "machinery-moving": {
    h1: "Machinery Moving & Equipment Lifting — Central Florida",
    metaTitle: "Machinery Moving Crane Services — Orlando & DeLand, FL",
    metaDescription:
      "Heavy machinery moving, loading, offloading and precision equipment placement across Central Florida. Generators, transformers, presses, CNCs, tanks and industrial equipment.",
    keywords:
      "machinery moving florida, equipment crane rental orlando, generator lift deland, transformer lift central florida, cnc machine moving",
    heroImage: MANITEX,
    intro:
      "Whether you are receiving a new CNC, dropping a standby generator, staging a transformer or relocating a printing press, Alliance Crane Service handles industrial machinery moves across Central Florida with the care they demand.",
    body: [
      "Machinery moves are a specialty — the equipment is heavy, expensive and unforgiving of a bad pick. We plan every move around the equipment specs: rigging points, center of gravity, floor loading, doorway clearances and set-down orientation. That is the difference between a smooth commissioning and a warranty claim.",
      "Our two-crane fleet handles the majority of light-industrial equipment moves in the DeLand, Orlando, Daytona, Jacksonville and Titusville markets. For truly oversized moves we team with local heavy-haul and millwright partners we trust.",
    ],
    benefits: [
      { title: "Spec-Driven Picks", body: "We read the equipment specs before we quote — lift points, weights and CG." },
      { title: "Damage-Free Handling", body: "Nylon slings, spreader bars and controlled speeds protect finished equipment." },
      { title: "Site Coordination", body: "We coordinate with millwrights, electricians and delivery trucks so nobody is standing around." },
      { title: "Setback & Foundation Care", body: "Ground protection and outrigger mats where needed to protect finished surfaces." },
    ],
    applications: [
      "Generator and transformer sets",
      "CNC and machine-tool placement",
      "Printing press and industrial equipment moves",
      "Storage tank and pressure vessel lifts",
      "Chiller and compressor swaps",
      "Boat and specialty equipment loading",
      "Trailer and shipping container placement",
    ],
    faqs: [
      {
        q: "Do you provide rigging?",
        a: "Standard rigging comes with the crane. Specialty rigging (spreader bars, lifting beams, engineered fixtures) can be sourced or coordinated with a rigging partner.",
      },
      {
        q: "Can you load and unload trucks?",
        a: "Yes — offloading from flatbeds and step-decks is one of our most common scopes. Give us the delivery ETA and we will meet the truck.",
      },
      {
        q: "How heavy of a machine can you move?",
        a: "Up to 50 tons under favorable conditions with the Manitex. Longer reaches and tighter working radiuses derate that number — we size every pick during the quote.",
      },
    ],
    related: ["hydraulic-mobile-crane", "emergency-lifting", "steel-erection"],
  },

  "truss-lifts": {
    h1: "Truss Lifts — Residential & Commercial Truss Setting in Central Florida",
    metaTitle: "Truss Lift Crane Services — Orlando, DeLand & Daytona",
    metaDescription:
      "Wood and steel truss crane setting for new homes, additions and commercial builds across Central Florida. Coordinated drops that keep your framing crew moving.",
    keywords:
      "truss crane florida, truss lift orlando, residential truss crane deland, commercial truss lift central florida, wood truss setting",
    heroImage: GROVE,
    intro:
      "Framers across Central Florida book Alliance Crane Service for one reason: we show up on time and keep the trusses coming. From single-family additions to multi-unit builds, we sequence drops so your crew stays busy and gets loaded out before dark.",
    body: [
      "Truss setting is a pace game. The best framers can burn through a package fast if the crane keeps up. We work with the crew leader on layout, stacking direction, and sequencing so bundles come off the truck, get lifted, and get set with almost no dead time.",
      "Because our cranes are highway-legal and quick to set, we can bounce between residential lots efficiently — perfect for production builders working multiple homes in a development.",
    ],
    benefits: [
      { title: "Framer-Paced", body: "We match the speed of your crew — no dead time between picks." },
      { title: "Residential & Commercial", body: "From single-story additions to production home builds and light-commercial roofs." },
      { title: "Wood & Steel Trusses", body: "Standard wood, LVL, engineered and light-steel trusses all handled." },
      { title: "Same-Week Booking", body: "Truss deliveries never quite land on the day you planned. We flex with you." },
    ],
    applications: [
      "Single-family residential trusses",
      "Home additions and remodels",
      "Multi-family and townhome builds",
      "Production home communities",
      "Light-commercial wood trusses",
      "Steel truss and joist setting",
      "Church, community and modular roofs",
    ],
    faqs: [
      {
        q: "How long does a typical residential truss set take?",
        a: "Most single-family homes take a half day to a full day depending on complexity, layout and how the trusses are stacked on the truck.",
      },
      {
        q: "Do we need to unload the truck first?",
        a: "No — in most cases we pick directly off the delivery truck, which is faster and safer for the trusses.",
      },
      {
        q: "Can you do truss lifts in developments with tight lots?",
        a: "Yes — the 40-ton Grove is compact enough for tight residential lots and neighborhood streets.",
      },
    ],
    related: ["crane-rental-operator", "hydraulic-mobile-crane", "steel-erection"],
  },

  "emergency-lifting": {
    h1: "24/7 Emergency Crane Service — Central Florida",
    metaTitle: "24/7 Emergency Crane Service — DeLand, Orlando & Daytona",
    metaDescription:
      "24/7 emergency lifting services in Central Florida. Storm response, tree-on-house, vehicle recovery, structural lifts and equipment failures — call Alliance Crane anytime.",
    keywords:
      "emergency crane service florida, storm crane response orlando, tree on house crane deland, 24 hour crane rental central florida, hurricane recovery crane",
    heroImage: MANITEX,
    intro:
      "When something has failed, fallen or collapsed, minutes matter. Alliance Crane Service operates a 24/7 emergency line for Central Florida — storms, structural failures, vehicle recovery, HVAC blowouts and after-hours industrial breakdowns.",
    body: [
      "Central Florida is hurricane country. When a tree comes down on a house, a carport peels off in a squall, or a rooftop unit dies in July, you need a crane in the air fast — with an operator who has done this before and can improvise safely under real conditions.",
      "Our emergency line rings a real dispatcher, not a call center. We assess the scope, quote the mobilization, and get moving. For insurance-covered work we can document the scene and coordinate with adjusters.",
    ],
    benefits: [
      { title: "24/7 Real Dispatch", body: "A live person answers our emergency line — day, night, weekend, holiday." },
      { title: "Storm Response Ready", body: "Post-hurricane and post-storm structural lifts, debris removal and roof stabilization." },
      { title: "Insurance-Friendly", body: "We document conditions and can work directly with your carrier or adjuster." },
      { title: "Fast Mobilization", body: "From DeLand we reach most Central Florida cities in 60–90 minutes." },
    ],
    applications: [
      "Tree-on-house / tree-on-vehicle removal",
      "Post-hurricane structural stabilization",
      "Roof and wall collapse response",
      "Vehicle, boat and equipment recovery",
      "Failed HVAC or generator emergency swaps",
      "Broken or fallen sign, pole or antenna",
      "Industrial equipment breakdowns",
    ],
    faqs: [
      {
        q: "How do I reach you after hours?",
        a: "Call (386) 506-7771 anytime — the same number rings 24/7 for emergency lifts across Central Florida.",
      },
      {
        q: "Do you work directly with insurance?",
        a: "Yes. For covered claims we can document the scene, provide itemized invoices and communicate with adjusters and restoration contractors.",
      },
      {
        q: "What is your response time?",
        a: "From DeLand we can typically be onsite in Orlando, Daytona, Sanford or the surrounding area within 60–90 minutes for true emergencies. Longer runs to Jacksonville or Titusville are quoted when the call comes in.",
      },
      {
        q: "Can you handle post-hurricane volume?",
        a: "We prioritize life-safety and structural stabilization first, then work through insured-property calls in the order they come in. Book early — do not wait for the storm to make landfall.",
      },
    ],
    related: ["crane-rental-operator", "hvac-placement", "machinery-moving"],
  },
};
